import { useState, MouseEvent } from "react";
import {
  Button,
  Menu,
  MenuItem,
  Box,
  ListItemIcon,
  ListItemText,
  IconButton,
} from "@mui/material";
import { IoChevronDownOutline, IoChevronUpOutline } from "react-icons/io5";

interface DropdownItem {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
}

interface SplitButtonProps {
  label: string;
  onClick: () => void;
  dropdownItems: DropdownItem[];
  color?: "primary" | "secondary" | "success" | "error" | "info" | "warning";
  variant?: "contained" | "outlined" | "text";
}

export default function SplitButton({
  label,
  onClick,
  dropdownItems,
  color = "primary",
  variant = "contained",
}: SplitButtonProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleToggleMenu = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation(); // prevent triggering main button click
    setAnchorEl(open ? null : event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box sx={{ display: "inline-flex", alignItems: "center" }}>
      <Button
        variant={variant}
        color={color}
        onClick={onClick}
        sx={{
          textTransform: "none",
          whiteSpace: "nowrap",
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2,
        }}
      >
        {/* 👇 Chevron at start */}
        <IconButton
          size="small"
          onClick={handleToggleMenu}
          sx={{
            p: 0.5,
            color: "inherit",
            mr: 1,
            "&:hover": { background: "transparent" },
          }}
        >
          {open ? <IoChevronUpOutline size={18} /> : <IoChevronDownOutline size={18} />}
        </IconButton>

        {/* Label */}
        {label}
      </Button>

      {/* Dropdown menu */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: { minWidth: 180 },
        }}
      >
        {dropdownItems.map((item, index) => (
          <MenuItem
            key={index}
            onClick={() => {
              item.onClick();
              handleClose();
            }}
          >
            {item.icon && (
              <ListItemIcon sx={{ minWidth: 32, color: "inherit" }}>
                {item.icon}
              </ListItemIcon>
            )}
            <ListItemText>{item.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
